import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, Car, ChevronRight, Plus, AlertTriangle } from "lucide-react";
import { api } from "../api/client";
import { PageHeader } from "../components/Layout";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { Button } from "../components/UIKit";
import {
  formatDate, formatKm, RULES_LABELS, statusColors, humanDelay, daysUntil, mapNetworkError,
} from "../lib/fleet";

export default function Alerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, d] = await Promise.all([api.get("/alerts"), api.get("/dashboard")]);
      setAlerts(a.data);
      setTotalVehicles(d.data.total_vehicles);
      setErrorMsg(null);
      setLoaded(true);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const urgent = alerts.filter((a) => a.status === "urgent");
  const upcoming = alerts.filter((a) => a.status === "upcoming");
  const hasVehicles = totalVehicles > 0;

  const subtitle = !loading
    ? urgent.length > 0
      ? `${urgent.length} urgente${urgent.length > 1 ? "s" : ""} · ${upcoming.length} à prévoir`
      : upcoming.length > 0
        ? `${upcoming.length} à prévoir`
        : hasVehicles ? "Rien à faire" : undefined
    : undefined;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <PageHeader title="Alertes" subtitle={subtitle} />

      <div style={{ padding: "0 20px 100px" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ height: 80, background: "var(--mf-bg-muted)", borderRadius: 16 }} />
            ))}
          </div>
        ) : errorMsg && !loaded ? (
          <ErrorState message={errorMsg} onRetry={() => { setLoading(true); load(); }} testID="alerts-error" />
        ) : !hasVehicles ? (
          <EmptyState
            testID="alerts-empty-no-vehicles"
            icon={<Car size={64} color="var(--mf-border)" />}
            title="Commencez votre flotte"
            subtitle="Ajoutez votre premier véhicule pour commencer à recevoir des alertes d'entretien et de documents."
            action={
              <Button
                testID="alerts-empty-add"
                icon={<Plus size={18} />}
                onClick={() => navigate("/vehicles/new")}
                fullWidth={false}
                style={{ paddingLeft: 22, paddingRight: 22 }}
              >
                Ajouter mon premier véhicule
              </Button>
            }
          />
        ) : alerts.length === 0 ? (
          <EmptyState
            testID="alerts-empty-all-good"
            icon={<CheckCircle size={64} color="var(--mf-ok-solid)" />}
            title="Tout est à jour"
            subtitle="Aucune intervention n'est nécessaire pour le moment. Votre flotte est en excellente santé."
          />
        ) : (
          <>
            {urgent.length > 0 && <Section title="Urgent" color="var(--mf-urgent-solid)">{urgent.map((a, i) => <AlertRow key={`u-${i}`} alert={a} onClick={() => navigate(`/vehicles/${a.vehicle_id}`)} />)}</Section>}
            {upcoming.length > 0 && <Section title="À prévoir" color="var(--mf-upcoming-solid)">{upcoming.map((a, i) => <AlertRow key={`p-${i}`} alert={a} onClick={() => navigate(`/vehicles/${a.vehicle_id}`)} />)}</Section>}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
        <h3 style={{
          margin: 0, fontSize: 12, fontWeight: 800,
          color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.6,
        }}>{title}</h3>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>
    </div>
  );
}

function AlertRow({ alert, onClick }) {
  const c = statusColors(alert.status);
  const delay = humanDelay(alert.next_date);
  const d = daysUntil(alert.next_date);
  const label =
    alert.type === "custom_maintenance" || alert.type === "autre_document"
      ? alert.label || RULES_LABELS[alert.type] || "Entretien"
      : RULES_LABELS[alert.type] || alert.label;
  return (
    <button
      onClick={onClick}
      data-testid={`alert-row-${alert.vehicle_id}-${alert.type}`}
      className="mf-anim-in"
      style={{
        width: "100%", background: "var(--mf-bg-card)", border: "none",
        borderRadius: 16, padding: 14, boxShadow: "var(--mf-shadow)",
        display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
        textAlign: "left", overflow: "hidden", position: "relative",
      }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: c.solid }} />
      <div style={{ flex: 1, marginLeft: 6, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)" }}>{label}</span>
          {delay ? (
            <span style={{
              padding: "3px 10px", borderRadius: 999,
              background: c.bg, color: c.text,
              fontSize: 11, fontWeight: 800, letterSpacing: 0.2,
              display: "inline-flex", alignItems: "center", gap: 4,
            }}>
              {d !== null && d < 0 && <AlertTriangle size={12} />}
              {delay}
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 13, color: "var(--mf-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {alert.vehicle_brand} {alert.vehicle_model} · {alert.vehicle_plate?.toUpperCase()}
        </div>
        <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>
          {alert.next_date ? formatDate(alert.next_date) : ""}
          {alert.next_date && alert.next_mileage ? " · " : ""}
          {alert.next_mileage ? formatKm(alert.next_mileage) : ""}
        </div>
      </div>
      <ChevronRight size={16} color="var(--mf-text-muted)" />
    </button>
  );
}
